import React, { forwardRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ModalNew from "../popup/Modal";
import { PopupHeader, PopupContent } from "../popup/common";

const DateInput = forwardRef(({ 
  className = "", 
  onChange, 
  onBlur, 
  value, 
  defaultValue,
  name,
  label,
  onOpen,
  onClose,
  maxDate,
  minDate,
  ...props 
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  // Internal state to track selected date when not controlled externally
  const [internalValue, setInternalValue] = useState(defaultValue || "");
  
  const handleOpen = () => {
    setIsOpen(true);
    if (onOpen) onOpen();
  };
  
  const handleClose = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  // Parse string date (YYYY-MM-DD) to Date object
  const parseDate = (dateString) => {
    if (!dateString) return null;
    if (dateString instanceof Date) return dateString;
    const date = new Date(dateString + "T00:00:00");
    return isNaN(date.getTime()) ? null : date;
  };

  // Format Date object to string (YYYY-MM-DD)
  const formatDate = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Format for display (MM/DD/YYYY)
  const formatDisplayDate = (date) => {
    if (!date) return "";
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleChange = (date) => {
    const formattedDate = formatDate(date);
    // Update internal state for uncontrolled usage
    setInternalValue(formattedDate);
    const syntheticEvent = {
      target: {
        name: name,
        value: formattedDate,
      },
    };
    if (onChange) {
      onChange(syntheticEvent);
    }
    handleClose();
  };

  const handleBlur = () => {
    if (onBlur) {
      const syntheticEvent = {
        target: {
          name: name,
          value: formatDate(parseDate(value || defaultValue)),
        },
      };
      onBlur(syntheticEvent);
    }
  };

  // Use controlled value if provided, otherwise use internal state
  const selectedDate = parseDate(value !== undefined ? value : internalValue);

  // Custom header with navigation arrows
  const renderCustomHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    
    // Determine max year and month based on maxDate
    const effectiveMaxDate = maxDate || new Date();
    const maxYear = effectiveMaxDate.getFullYear();
    const maxMonth = effectiveMaxDate.getMonth();
    const minYear = minDate ? minDate.getFullYear() : maxYear - 100;
    
    // Build years array respecting min and max
    const years = [];
    for (let i = maxYear; i >= minYear; i--) {
      years.push(i);
    }

    const selectedMonth = months[date.getMonth()];
    const selectedYear = date.getFullYear();
    const isCurrentYear = selectedYear === maxYear;

    return (
      <div className="datepicker-custom-header">
        <div className="datepicker-month-year">
          <div className="datepicker-select-wrapper">
            <span className="datepicker-select-display">{selectedMonth}</span>
            <select
              value={date.getMonth()}
              onChange={({ target: { value } }) => changeMonth(Number(value))}
              className="datepicker-select-hidden"
            >
              {months.map((month, index) => {
                // Disable months beyond maxMonth if we're in the max year
                const isDisabled = isCurrentYear && index > maxMonth;
                return (
                  <option key={month} value={index} disabled={isDisabled}>
                    {month}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="datepicker-select-wrapper">
            <span className="datepicker-select-display">{selectedYear}</span>
            <select
              value={selectedYear}
              onChange={({ target: { value } }) => changeYear(Number(value))}
              className="datepicker-select-hidden"
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="datepicker-nav-group">
          <button
            type="button"
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
            className="datepicker-nav-btn"
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/icons/date_picker_left_arrow_white.svg`}
              alt="Previous"
            />
          </button>

          <button
            type="button"
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            className="datepicker-nav-btn"
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/icons/date_picker_right_arrow_white.svg`}
              alt="Next"
            />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Hidden input for native form submission */}
      {name && (
        <input
          type="hidden"
          name={name}
          value={selectedDate ? formatDate(selectedDate) : ""}
        />
      )}
      <div className="date-input-wrapper" onClick={handleOpen}>
        <input
          ref={ref}
          type="text"
          className={`form-input ${className}`}
          value={selectedDate ? formatDisplayDate(selectedDate) : ""}
          onBlur={handleBlur}
          placeholder="MM/DD/YYYY"
          readOnly
          {...props}
        />
        <img
          src={`${process.env.PUBLIC_URL}/images/icons/calendar_blue.svg`}
          alt="Calendar"
          className="date-input-icon"
        />
      </div>

      {isOpen && (
        <ModalNew className="content-modal" style={{ maxWidth: "320px" }}>
          <PopupHeader
            title={label || "Select Date"}
            setOpenState={(open) => { if (!open) handleClose(); }}
            icon={`${process.env.PUBLIC_URL}/images/icons/calendar_blue.svg`}
            variant="compact"
          />
          <PopupContent>
            <div className="datepicker-modal-container">
              <DatePicker
                selected={selectedDate}
                onChange={handleChange}
                renderCustomHeader={renderCustomHeader}
                maxDate={maxDate}
                minDate={minDate}
                inline
              />
            </div>
          </PopupContent>
        </ModalNew>
      )}
    </>
  );
});

DateInput.displayName = "DateInput";

export default DateInput;
